import { Box, Tooltip, Typography } from "@mui/joy";
import {
  DataGridPremium,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-premium";
import { Gig } from "../../entities/gig";
import { compatibleSources } from "../../global";
import { useContractors } from "../../redux/contractor/contractorHooks";
import { useGigs } from "../../redux/gig/gigHooks";
import { useProjects } from "../../redux/project/projectHooks";
import { useStages } from "../../redux/stage/stageHooks";
import { useVenues } from "../../redux/venue/venueHooks";
import { Tab, Tabs } from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { setFormOpen, setSelectedGigIDs } from "../../reducers/rhapsody";
import { ServiceViewer } from "../servicesList/serviceViewer";

/**
 *
 * @param root0
 * @param root0.tabs
 * @param root0.upcoming
 * @param root0.projectID
 * @param root0.venueID
 * @param root0.contractorID
 * @returns {ReactElement} GigsDatagrid page
 */
export function GigsDatagrid({
  tabs = false,
  upcoming = false,
  projectID,
  venueID,
  contractorID,
}: {
  tabs?: boolean;
  upcoming?: boolean;
  projectID?: number;
  venueID?: number;
  contractorID?: number;
}) {
  const { gigs, isLoading } = useGigs();
  const { contractors } = useContractors();
  const { projects } = useProjects();
  const { venues } = useVenues();
  const { stages } = useStages();
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [viewer, setViewer] = useState<{ url: string; title?: string } | null>(
    null
  );

  let _gigs = [...gigs];

  // Resolve the venue's IANA tz for a gig so day-bucketing (completed /
  // daysLeft) happens against the venue's wall clock, not the browser's.
  const tzForGig = (gig: Gig) =>
    venues?.find(
      (e) =>
        compatibleSources(e.sourceID, gig.sourceID) &&
        e.sourceRecordID == gig.venueID
    )?.tzName;

  const toBeDeterminedGigs = gigs.filter(
    (g) => g.dateFromUTC === "0001-01-01T00:00:00Z"
  );

  if (tabs || upcoming) {
    if (tab == 0) {
      _gigs = _gigs.filter((g) => !g.completed(tzForGig(g)));
      _gigs.sort((a, b) => a.dateFromUTC.localeCompare(b.dateFromUTC));
    }
    if (tab == 1) {
      _gigs = _gigs.filter((g) => g.completed(tzForGig(g)));
      _gigs.sort((a, b) => b.dateFromUTC.localeCompare(a.dateFromUTC));
    }
    if (tab == 2) {
      _gigs = _gigs.filter((g) => g.toBeDetermined());
    }
  } else {
    _gigs.sort((a, b) => b.dateFromUTC.localeCompare(a.dateFromUTC));
  }

  if (projectID) {
    _gigs = _gigs.filter((g) => g.projectID === projectID);
  }

  if (venueID) {
    _gigs = _gigs.filter((g) => g.venueID === venueID);
  }

  if (contractorID) {
    _gigs = _gigs.filter((g) => g.contractorID === contractorID);
  }

  console.log(gigs);
  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        width: "100%",
      }}
    >
      {tabs ? (
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label="Upcoming" sx={{ textTransform: "none" }} />
          <Tab label="Past" sx={{ textTransform: "none" }} />
          {toBeDeterminedGigs?.length ? (
            <Tab label="To be determined" sx={{ textTransform: "none" }} />
          ) : (
            []
          )}
        </Tabs>
      ) : (
        []
      )}
      <DataGridPremium
        slots={{
          toolbar: Toolbar,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        pagination
        pageSizeOptions={[25]}
        hideFooter={upcoming}
        sx={{ flexGrow: 1 }}
        loading={isLoading}
        onCellClick={(e) => {
          const gig = e.row as Gig;
          if (gig.sourceID === 420) {
            dispatch(
              setSelectedGigIDs({
                id: gig.id,
                sourceRecordID: gig.sourceRecordID,
                sourceID: gig.sourceID,
              })
            );
            dispatch(setFormOpen({ isOpen: true, formID: "gig" }));
          } else {
            const url = gig.getInviteURL();
            if (url) setViewer({ url, title: gig.title ?? "Service" });
          }
        }}
        density="comfortable"
        rows={_gigs ?? []}
        initialState={{
          pinnedColumns: {
            right: ["stageID"],
          },
          pagination: {
            paginationModel: {
              pageSize: 25,
            },
          },
        }}
        columns={[
          {
            field: "sourceID",
            headerName: "",
            width: 12,
            renderCell: (p) =>
              p.value !== 420 ? (
                <Tooltip
                  arrow
                  size="sm"
                  sx={{ maxWidth: 150, textAlign: "center" }}
                  variant="outlined"
                  title="Thie item cannot be edited. It is controlled by an organization using Rhapsody."
                >
                  <i className="fa-solid fa-lock"></i>
                </Tooltip>
              ) : (
                <Box />
              ),
          },
          {
            field: "dateToUTC",
            headerName: "Date",
            width: 200,
            valueGetter: (c) => {
              const gig = c.row;
              const venue = venues?.find(
                (e) =>
                  compatibleSources(e.sourceID, gig.sourceID) &&
                  e.sourceRecordID == gig.venueID
              );
              const tz = venue?.tzName;
              const ret = [gig?.formattedDate(tz)];
              if (!gig.toBeDetermined() && !gig.noTime(tz)) {
                ret.push(gig?.startTime(tz));
                ret.push(gig?.endTime(tz));
                ret.push(gig?.from(tz).format("z"));
              }
              return ret.join(", ");
            },
            renderCell: (c) => {
              const gig = c.row;
              const venue = venues?.find(
                (e) =>
                  compatibleSources(e.sourceID, gig.sourceID) &&
                  e.sourceRecordID == gig.venueID
              );
              const tz = venue?.tzName;

              return (
                <Box>
                  <Typography level="body2">
                    <b>{gig?.formattedDate(tz)}</b>
                  </Typography>
                  {!gig.toBeDetermined() && !gig.noTime(tz) ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Typography
                        startDecorator={
                          <i className="fa-solid fa-circle-arrow-up-right"></i>
                        }
                        level="body3"
                      >
                        {gig?.startTime(tz)}
                      </Typography>
                      <Typography
                        startDecorator={
                          <i className="fa-solid fa-circle-arrow-down-right"></i>
                        }
                        level="body3"
                      >
                        {gig?.endTime(tz)}
                      </Typography>
                      <Typography level="body3">
                        {gig?.from(tz).format("z")}
                      </Typography>
                    </Box>
                  ) : (
                    []
                  )}
                </Box>
              );
            },
          },
          {
            field: "coverID",
            headerName: "Organization",
            width: 250,
            valueGetter: (c) => {
              const contractor = contractors?.find(
                (e) =>
                  compatibleSources(e.sourceID, c.row.sourceID) &&
                  e.sourceRecordID == c.row.contractorID
              );

              return contractor?.name;
            },
            renderCell: (c) => {
              const contractor = contractors?.find(
                (e) =>
                  compatibleSources(e.sourceID, c.row.sourceID) &&
                  e.sourceRecordID == c.row.contractorID
              );

              return contractor?.coverURL ? (
                <Tooltip size="sm" arrow title={contractor?.name}>
                  <Box
                    sx={{
                      background: contractor.color,
                      p: 0.5,
                      borderRadius: "8px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={contractor.coverURL}
                      height={25}
                      style={{ borderRadius: 8 }}
                    />
                  </Box>
                </Tooltip>
              ) : (
                <Typography level="body2">{contractor?.name}</Typography>
              );
            },
          },
          {
            field: "name",
            headerName: "Description",
            width: 250,
            valueGetter: (c) => {
              const gig = c.row;
              const project = projects?.find(
                (e) =>
                  compatibleSources(e.sourceID, c.row.sourceID) &&
                  e.sourceRecordID == c.row.projectID
              );
              const ret = [gig?.title ?? "Untitled Service"];
              if (project) ret.push(project?.name);
              return ret.join(", ");
            },
            renderCell: (c) => {
              const gig = c.row;
              const project = projects?.find(
                (e) =>
                  compatibleSources(e.sourceID, c.row.sourceID) &&
                  e.sourceRecordID == c.row.projectID
              );

              return (
                <Box>
                  <Typography level="body2">
                    <b>{gig?.title ?? "Untitled Service"}</b>
                  </Typography>
                  {project ? (
                    <Typography level="body3">{project?.name}</Typography>
                  ) : (
                    []
                  )}
                </Box>
              );
            },
          },
          {
            width: 150,
            align: "center",
            field: "dateFromUTC",
            headerAlign: "center",
            headerName: "Remaining",
            valueGetter: (e) => {
              const gig = e.row;
              const tz = tzForGig(gig);
              if (gig.toBeDetermined()) return "N/A";
              const inDays = gig.daysLeft(tz);
              if (gig.completed(tz)) return "Completed";
              return `in ${inDays} day${inDays > 1 ? "s" : ""}`;
            },
            renderCell: (e) => {
              const gig = e.row;
              const tz = tzForGig(gig);
              if (gig.toBeDetermined())
                return (
                  <Typography>
                    <i className="fa-solid fa-question"></i>
                  </Typography>
                );
              const inDays = gig.daysLeft(tz);
              if (gig.completed(tz))
                return (
                  <Typography color="success">
                    <i className="fa-solid fa-check"></i>
                  </Typography>
                );
              return (
                <Typography
                  level="h6"
                  sx={{ textAlign: "center", lineHeight: 1 }}
                >
                  <b>{inDays}</b>
                  <br />
                  <Typography level="body3">
                    {inDays != 1 ? "DAYS" : "DAY"}
                  </Typography>
                </Typography>
              );
            },
          },
          {
            field: "venueID",
            headerName: "Venue",
            width: 200,
            valueGetter: (c) => {
              if (c.row.venueID === -1)
                return (
                  <Typography level="body2">
                    <b>Venue to be determined</b>
                  </Typography>
                );
              const venue = venues?.find(
                (e) =>
                  compatibleSources(e.sourceID, c.row.sourceID) &&
                  e.sourceRecordID == c.row.venueID
              );

              if (venue)
                return `${venue?.name} ${venue?.address} ${venue?.city}, ${venue?.state} ${venue?.zipcode}`;
            },
            renderCell: (c) => {
              if (c.row.venueID === -1)
                return (
                  <Typography level="body2">
                    <b>Venue to be determined</b>
                  </Typography>
                );
              const venue = venues?.find(
                (e) =>
                  compatibleSources(e.sourceID, c.row.sourceID) &&
                  e.sourceRecordID == c.row.venueID
              );

              return (
                <Box>
                  <Typography level="body2">
                    <b>{venue?.name ?? "Unkown Venue"}</b>
                  </Typography>
                  {venue ? (
                    <Typography level="body3">
                      {venue?.address} {venue?.city}, {venue?.state}{" "}
                      {venue?.zipcode}
                    </Typography>
                  ) : (
                    []
                  )}
                </Box>
              );
            },
          },
          {
            field: "stageID",
            align: "right",
            headerAlign: "right",
            headerName: "Status",
            width: 200,
            valueGetter: (c) => {
              const gig = c.row;
              const stage = stages?.find((s) => s.id === gig.stageID);
              return stage?.wording();
            },
            renderCell: (c) => {
              const gig = c.row;
              const stage = stages?.find((s) => s.id === gig.stageID);
              if (gig.stageID > 0 && gig.isPlaying())
                return (
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {gig.isCattleCall() ? (
                      <i
                        className="fa-solid fa-cowbell fa-shake"
                        style={{ fontSize: 18, color: stage?.color }}
                      />
                    ) : (
                      <img src={`${stage?.icon}`} style={{ height: 18 }} />
                    )}
                    <Typography level="body2" sx={{ color: stage?.color }}>
                      <b>{stage?.wording()}</b>
                    </Typography>
                  </Box>
                );

              return <Box />;
            },
          },
        ]}
      />
      <ServiceViewer
        url={viewer?.url ?? null}
        title={viewer?.title}
        onClose={() => setViewer(null)}
      />
    </Box>
  );
}

export function Toolbar() {
  return (
    <Box
      className="header"
      sx={{
        display: "flex",
        justifyContent: "space-between",
        p: 1,
        gap: 2,
        alignItems: "center",
      }}
    >
      <GridToolbarQuickFilter
        size="small"
        fullWidth
        sx={{ flexGrow: 1 }}
        variant="outlined"
      />
      <GridToolbarExport />
    </Box>
  );
}
